import {resolveBackground} from '#core/utils/color-utils.js';
import {merge} from '#core/utils/config-utils.js';
import {icon} from '#core/utils/style-utils.js';

import {generateParticipants} from '#core/application/modules/tiles/participants/index.js';
import {generateStreamingIndicator} from '#core/application/modules/tiles/streaming-indicator/index.js';
import {generateEventTimingStatus} from '#core/application/modules/tiles/event-timing-status/index.js';
import {generateRunnerInfo} from '#core/application/modules/tiles/runner-info/index.js';
import {generateMiniScoreboard} from '#core/application/modules/tiles/mini-scoreboard/index.js';
import {generatePriceBoostLabel} from '#core/application/modules/tiles/price-boost-label/index.js';
import {generatePriceBoostItem} from '#core/application/modules/tiles/price-boost-item/index.js';
import {generateBetBuilderSelectionItem} from '#core/application/modules/tiles/bet-builder-selection-item/index.js';

export function eventCardsFactory(PALETTE) {
    const {FONT} = PALETTE;

    const BASE_EVENT_CARD_CONTAINER = {
        style: {
            fontFamily: FONT.FAMILY,
            fontSize: FONT.SIZE_MEDIUM,
            borderRadius: PALETTE.CARD_BORDER_RADIUS,
            ...resolveBackground(PALETTE.CARD_CONTAINER_BG),
            height: '100%',

            ...(PALETTE.CARD_BORDER_WIDTH && PALETTE.CARD_BORDER_COLOR !== 'none'
                ? {
                      borderWidth: PALETTE.CARD_BORDER_WIDTH,
                      borderColor: PALETTE.CARD_BORDER_COLOR,
                      borderStyle: 'solid',
                  }
                : undefined),
            ...(PALETTE.CARD_CONTAINER_SHOW_SHADOW
                ? {
                      boxShadow: PALETTE.CARD_CONTAINER_SHADOW,
                  }
                : {}),
        },
    };
    const SHOW_ALL_LABEL = {
        container: {
            flexShrink: 0,
        },
        style: PALETTE.CARD_SHOW_ALL_LINK.SECONDARY_LINK_TEXT,
    };
    const SHOW_ALL_ICON = {
        style: {
            fill: PALETTE.CARD_SHOW_ALL_LINK.SECONDARY_LINK_ICON_COLOR,
        },
    };
    const BET_NOW_LABEL = {
        style: PALETTE.CARD_BET_NOW_LINK.SECONDARY_LINK_TEXT,
    };
    const BET_NOW_ICON = {
        style: {
            fill: PALETTE.CARD_BET_NOW_LINK.SECONDARY_LINK_ICON_COLOR,
        },
    };
    const EVENT_CARD_SPORT_ICON = {
        style: {
            fill: PALETTE.CARD_SPORT_ICON_COLOR,
        },
    };
    const WATERMARK_ICON = {
        style: {
            fill: PALETTE.CARD_WATERMARK_ICON_COLOR,
            opacity: PALETTE.CARD_WATERMARK_ICON_OPACITY,
        },
    };

    const CARD_EVENT_NAME_TEXT = {
        fontSize: FONT.SIZE_XSMALL,
        fontWeight: FONT.WEIGHT_NORMAL,
        ...PALETTE.CARD_EVENT_NAME_TEXT,
    };
    const CARD_TIMING_STATUS = generateEventTimingStatus(PALETTE.CARD_TIMING_STATUS);
    const MARKET_NAME_STYLE = {
        fontSize: FONT.SIZE_SMALL,
        ...PALETTE.CARD_MARKET_NAME_TEXT,
    };
    const PRICE_BOOST_LABEL = generatePriceBoostLabel(PALETTE.CARD_SELECTION_PRICE_BOOST_LABEL);
    const PRICE_BOOST_ITEM = generatePriceBoostItem(PALETTE.CARD_SELECTION_PRICE_BOOST_ITEM);
    const CARD_SELECTION_ITEM = generateBetBuilderSelectionItem(PALETTE.CARD_MATCH_ACCA_SELECTION_ITEM);

    return {
        BaseEventCard: {
            container: BASE_EVENT_CARD_CONTAINER,
            title: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_SEMI_BOLD,
                    ...PALETTE.CARD_BASE_HEADER_TEXT,
                },
            },
            clickableArea: {
                style: {
                    base: {
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: 'auto',
                    },
                },
            },
        },
        FallbackEventCard: {
            betNowContainer: {
                style: {
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: 'auto',
                },
            },
            betNowText: BET_NOW_LABEL,
            betNowIcon: BET_NOW_ICON,
            contentText: {
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    whiteSpace: 'normal',
                    ...PALETTE.CARD_FALLBACK_TEXT,
                },
            },
            backgroundImage: WATERMARK_ICON,
        },
        InplayFallbackEventCard: {
            title: '$EVENT_CARDS.FALLBACK.INPLAY.TITLE',
            content: '$EVENT_CARDS.FALLBACK.INPLAY.CONTENT',
        },
        SportFallbackEventCard: {
            view: {
                backgroundImageContainer: {
                    style: {
                        overflow: 'hidden',
                    },
                },
            },
        },
        OutrightEventCard: {
            eventCardBase: {
                container: {
                    style: {
                        justifyContent: 'space-between',
                    },
                },
                title: {
                    style: CARD_EVENT_NAME_TEXT,
                },
            },

            topContainer: {
                style: {
                    justifyContent: 'space-between',
                },
            },
            headerContainer: {
                style: {
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: 'auto',
                    justifyContent: 'initial', // fixing wrong mojito style
                },
            },
            marketHeader: {
                style: {
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: 'auto',
                },
            },
            marketNameLabel: {
                style: MARKET_NAME_STYLE,
            },
            showAllLabel: SHOW_ALL_LABEL,
            showAllIcon: SHOW_ALL_ICON,
            sportIcon: EVENT_CARD_SPORT_ICON,
            backgroundSportImage: WATERMARK_ICON,
        },
        SelectionCard: {
            sportIcon: EVENT_CARD_SPORT_ICON,
            boostedBackgroundSportImage: WATERMARK_ICON,
            genericBackgroundSportImage: WATERMARK_ICON,
            genericEventCard: {
                container: {
                    createStackingContext: true,
                },
                title: {
                    truncate: true,
                    ellipsisOnLineNumber: 1,
                    container: {
                        flexGrow: 0,
                    },
                    style: CARD_EVENT_NAME_TEXT,
                },
            },
            boostedEventCard: {
                container: {
                    createStackingContext: true,
                },
                title: {
                    truncate: true,
                    ellipsisOnLineNumber: 1,
                    style: CARD_EVENT_NAME_TEXT,
                },
                clickableArea: {
                    style: {
                        base: {
                            padding: '0 0 8px',
                        },
                    },
                },
            },
            headerTime: CARD_TIMING_STATUS,
            headerLiveLabel: CARD_TIMING_STATUS.liveText,
            selectionName: {
                style: {
                    lineHeight: '1.13', // better to set it here and store it as a string, cool Moj decision (selection-card.jsx:24)
                    fontSize: FONT.SIZE_LARGE,
                    fontWeight: FONT.WEIGHT_SEMI_BOLD,
                    ...PALETTE.CARD_SELECTION_NAME_TEXT,
                },
            },
            marketName: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_SEMI_BOLD,
                    ...PALETTE.CARD_SELECTION_MARKET_TEXT,
                },
            },
            selectionButton: PRICE_BOOST_ITEM.selectionButton,
            priceBoostContainer: {
                style: {
                    ...resolveBackground(PALETTE.CARD_SELECTION_PRICE_BOOST_CONTAINER_BG),
                },
            },
            priceBoostTitleAndImageContainer: PRICE_BOOST_LABEL.container,
            priceBoostImageContainer: PRICE_BOOST_LABEL.iconContainer,
            priceBoostImage: PRICE_BOOST_LABEL.icon,
            priceBoostTitle: PRICE_BOOST_LABEL.label,
            basePriceContainer: {
                style: {
                    alignItems: 'center',
                },
            },
            basePriceTitle: PRICE_BOOST_ITEM.wasTitle,
            basePriceAmount: PRICE_BOOST_ITEM.wasPrice,
            basePriceImage: PRICE_BOOST_ITEM.indicationIcon,
        },
        MatchEventCard: {
            eventCard: {
                title: {
                    truncate: true,
                    container: {
                        maxWidth: 195,
                    },
                    style: {
                        ...CARD_EVENT_NAME_TEXT,
                    },
                },
            },
            genericMarketContent: {
                container: {
                    style: {
                        backgroundColor: 'transparent',
                        backgroundImage: 'none',
                    },
                },
                market: {
                    selectionButton: {
                        view: {
                            infoText: {
                                ellipsisOnLineNumber: 1,
                            },
                        },
                    },
                },
            },
            gameLineMarketContent: {
                container: {
                    style: {
                        flexBasis: 0,
                        backgroundColor: 'transparent',
                        backgroundImage: 'none',
                    },
                },
                marketsContainer: {
                    style: {
                        _mobile_flexBasis: 'auto',
                    },
                },
                market: {
                    headerLabel: {
                        style: {
                            fontWeight: FONT.WEIGHT_SEMI_BOLD,
                            ...PALETTE.CARD_GAMELINE_MARKET_HEADER_TEXT,
                        },
                    },
                },
                participants: {
                    showTeamLogo: false, // should only enable for northstar for now
                    showShortName: false,
                    text: {
                        style: {
                            ...PALETTE.CARD_PARTICIPANTS.PARTICIPANTS_TEXT,
                        },
                    },
                    textWrapper: {
                        style: {
                            justifyContent: 'center',
                        },
                    },
                },
                scoreboard: generateMiniScoreboard(PALETTE.CARD_MINI_SCOREBOARD),
            },
            headerContainer: {
                style: {
                    justifyContent: 'initial',
                    alignItems: 'center',
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: 'auto',
                },
            },
            marketTitle: {
                style: MARKET_NAME_STYLE,
            },
            headerTime: CARD_TIMING_STATUS,
            sportIcon: EVENT_CARD_SPORT_ICON,
            backgroundSportImage: WATERMARK_ICON,
            usBackgroundSportImage: WATERMARK_ICON,
            // TODO: replace participantsAndScoreContainer and usParticipantsAndScoreContainer with eventListITemDetails generator usage
            eventItemDetails: {
                participants: merge(generateParticipants(PALETTE.CARD_PARTICIPANTS), {
                    showStartingPitcher: true,
                    showTeamLogo: false, // should only enable for northstar for now
                    text: {
                        container: {
                            display: 'flex',
                            padding: '2px 0', // same as scoreText in miniScoreboard, see DBX-7944
                        },
                    },
                }),
                scoreboard: generateMiniScoreboard(PALETTE.CARD_MINI_SCOREBOARD),
            },
        },
        BannerCard: {
            eventCard: {
                container: {
                    style: {
                        minHeight: '100%',
                    },
                },
            },
        },
        RaceCardPreview: {
            eventCardBase: {
                container: {
                    style: {
                        height: 'initial', // for RaceCardPreview height must be dynamic (i think)
                    },
                },
                clickableArea: {
                    style: {
                        base: {
                            minHeight: 32,
                        },
                    },
                },
            },
            runnerInfo: generateRunnerInfo(PALETTE.CARD_RUNNER_INFO),
            raceNameLabel: {
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    ...PALETTE.CARD_RACE_INFO_TEXT,
                },
            },
            racingSportIcon: EVENT_CARD_SPORT_ICON,
            raceDistanceLabel: {
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    ...PALETTE.CARD_RACE_INFO_TEXT,
                },
            },
            showAllLabel: SHOW_ALL_LABEL,
            showAllIcon: SHOW_ALL_ICON,
            streamingIndicator: generateStreamingIndicator(PALETTE.CARD_STREAMING_INDICATOR),
        },
        EventCardSkeleton: {
            container: BASE_EVENT_CARD_CONTAINER,
        },
        RaceCardsSkeleton: {
            raceCardContainer: BASE_EVENT_CARD_CONTAINER,
            title: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_SEMI_BOLD,
                    ...PALETTE.CARD_BASE_HEADER_TEXT,
                },
            },
        },
        PrebuiltBetsSkeleton: {
            container: {
                style: {
                    _mobile_padding: '0 8px',
                },
            },
            cardContainer: BASE_EVENT_CARD_CONTAINER,
        },
        EventCardsCarousel: {
            fallbackSportId: GENBET_BASED ? 'soccer' : 'tennis',
            swiper: {
                style: {
                    height: 'initial',
                    maxHeight: 200,
                },
            },
        },
        PrebuiltBet: {
            headerContainer: {
                style: {
                    flexGrow: 0,
                },
            },
            sportIcons: EVENT_CARD_SPORT_ICON,
            matchAccaIcon: merge(icon(PALETTE.CARD_MATCH_ACCA_BB_BET_ICON_COLOR, 'system-betbuilder'), {
                style: {
                    width: 16,
                },
            }),
            genericBackgroundSportImage: WATERMARK_ICON,
            eventName: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_BOLD,
                    wordBreak: 'break-word',
                    ...PALETTE.CARD_MATCH_ACCA_EVENT_NAME_TEXT,
                },
            },
            startTime: {
                style: {
                    fontSize: FONT.SIZE_XSMALL,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.CARD_MATCH_ACCA_EVENT_INFO_TEXT,
                },
            },
            typeName: {
                style: {
                    fontSize: FONT.SIZE_XSMALL,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.CARD_MATCH_ACCA_EVENT_INFO_TEXT,
                },
            },
            matchAccaSelectionsContainer: {
                style: {
                    flexGrow: 0,
                },
            },
            matchAccaSelectionItem: merge(CARD_SELECTION_ITEM, {
                selectionIcon: icon(PALETTE.CARD_MATCH_ACCA_SELECTION_ITEM.BB_BET_PATH_LINE_COLOR),
                marketName: {
                    style: {
                        fontSize: FONT.SIZE_XSMALL,
                    },
                },
                selectionLabel: {
                    style: {
                        overflowWrap: 'anywhere',
                    },
                },
            }),
        },
    };
}
